import { useCallback, useEffect, useState } from 'react';

import throttle from '../../utils/throttle';

const ESCAPE = 'Escape';

type ToggleOptions = {
  isShown: boolean;
  show: () => void;
  hide: () => void;
};

export interface ToggleContentProps {
  toggle: (show: ToggleOptions['show'], options: ToggleOptions) => JSX.Element;
  content: (hide: ToggleOptions['hide'], options: ToggleOptions) => JSX.Element;
  open?: boolean;
}

const ToggleContent = ({
  toggle,
  content,
  open = false,
}: ToggleContentProps) => {
  const [isShown, setIsShown] = useState(open);
  const show = useCallback(() => setIsShown(true), []);
  const hide = useCallback(() => setIsShown(false), []);

  const options = { isShown, show, hide };

  useEffect(() => {
    const handleKeyDown = throttle((event: KeyboardEvent) => {
      if (event.key !== ESCAPE) {
        return;
      }

      setIsShown(false);
    });

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      {toggle(show, options)}
      {isShown && content(hide, options)}
    </>
  );
};

export default ToggleContent;
